/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 22/06/22
 */

import React from 'react';
import { Box, Heading, Link, Text, HStack, VStack, OrderedList, ListItem, UnorderedList } from '@chakra-ui/react';
import usePageTitle from '@/utils/hooks/usePageTitle';
import FocusHeader from '@/components/FocusHeader/FocusHeader';

const Privacy = () => {
    usePageTitle('Privacy Notice');
    return (
        <Box maxH={'auto'} w={'100%'} p={6}>
            <HStack justifyContent={'center'}>
                <Box bg="offWhite" rounded="none" p={6} maxW={900}>
                    <VStack alignItems="flex-start" spacing={6} w="full">
                        <FocusHeader>Privacy management: UTS Work Planning Tool</FocusHeader>

                        <VStack alignItems="flex-start" spacing={2}>
                            <Heading as="h3" size="md">
                                Purpose
                            </Heading>
                            <Text>
                                The UTS Work Planning Tool (WPT) has been designed to support work planning for
                                academics and replace manual and paper based work planning and review. Academic staff
                                and their supervisors will be able to develop annual workplans, estimate workload, share
                                information, undertake mid and end of year reviews and see reports on their planning and
                                qualitative results. Records relating to work planning and performance reviews will
                                still be submitted to the People Unit (PU).
                            </Text>
                        </VStack>

                        <VStack alignItems="flex-start" spacing={2}>
                            <Heading as="h3" size="md">
                                User requirements and obligations
                            </Heading>
                            <Text>
                                Information contained within, or extracted from the WPT will contain information
                                classified as “sensitive”, and in some cases “confidential”. As user of the WPT tool,
                                you must comply with the{' '}
                                <Link href="https://www.gsu.uts.edu.au/policies/code-conduct.html" isExternal>
                                    Code of Conduct
                                </Link>
                                , the{' '}
                                <Link href="https://www.gsu.uts.edu.au/policies/privacy.html" isExternal>
                                    Privacy Policy
                                </Link>{' '}
                                and the{' '}
                                <Link href="https://www.gsu.uts.edu.au/policies/recordsmgmt.html" isExternal>
                                    Records Management Policy
                                </Link>{' '}
                                at all times in relation to their handling of this information. As a user, you must
                                treat this information confidentially and only use the information for the purposes
                                outlined above.
                            </Text>
                        </VStack>

                        <VStack alignItems={'flex-start'} spacing={2}>
                            <Heading as="h3" size="md">
                                Privacy notice
                            </Heading>
                            <Text>
                                Personal information collected and generated as part of the WPT and the workplanning and
                                review processes is covered by the{' '}
                                <Link
                                    href="https://www.uts.edu.au/staff/working-uts/employee-privacy-notice"
                                    isExternal
                                >
                                    employee privacy notice
                                </Link>
                                . Refer to the employee privacy notice for further information about how the university
                                collects, uses, discloses, accesses, corrects and manages employment information
                                collected and created about you as a staff member or affiliate of UTS, including for
                                workplanning and review purposes.
                            </Text>
                            <Text>
                                If you have any concerns or queries regarding your employee information or employee
                                privacy, refer to the employee privacy contacts section of the employee privacy notice
                                referred to above.
                            </Text>
                            <Text>
                                Some additional detail specific to how your information is collected and how access is
                                managed in relation to the WPT tool is provided below.
                            </Text>

                            <Heading as="h4" size="sm" paddingTop={2}>
                                Collection of information for the WPT platforms
                            </Heading>
                            <Text>
                                Information will be collected from you and your supervisor as part of workplanning and
                                review processes. Information will also be collected from existing information systems.
                                The WPT comprises two separate parts:
                            </Text>
                            <OrderedList paddingLeft={5}>
                                <ListItem marginTop={3}>
                                    Online planning platform: This platform is delivered by UTS Rapido and collects
                                    relevant information from the following information systems:
                                    <UnorderedList paddingLeft={3}>
                                        <ListItem>NEO – staff details related to an individual’s assignment</ListItem>
                                        <ListItem>Ascender – annual leave balances</ListItem>
                                        <ListItem>CASS – class name, size</ListItem>
                                        <ListItem>
                                            Research Master – research project data including lead CI, partner and
                                            income
                                        </ListItem>
                                    </UnorderedList>
                                    The planning platform is used by all academic staff and supervisors (excluding
                                    professional, casual and SSG staff), with minor modifications to enable all
                                    faculties to utilise the same tool and database.
                                </ListItem>
                                <ListItem marginTop={3}>
                                    Online reporting platform: This platform will directly link to the reporting
                                    platform (PowerBI) to provide analysis, work and progress reporting throughout the
                                    year for academic staff, relevant faculty staff and relevant administrative staff.
                                    PowerBI reports will combine academic staff’s planning and progress data (entered by
                                    the academic staff member) with qualitative information collected from the following
                                    information systems:
                                    <UnorderedList paddingLeft={3}>
                                        <ListItem>
                                            MyResearcherDashboard – for research income, publication and HDR data
                                        </ListItem>
                                        <ListItem>
                                            RAPT/FEIT-CA/CAD – for subjects, students, number of hours assigned to
                                            academic staff
                                        </ListItem>
                                    </UnorderedList>
                                </ListItem>
                            </OrderedList>

                            <Heading as="h4" size="sm" paddingTop={2}>
                                How access to data within the WPT is managed
                            </Heading>
                            <Text>
                                Each Faculty has access to their own data within the online planning platform. This
                                access is provided to staff on a needs basis only.
                            </Text>
                            <Text>Access to the PowerBI reports is managed through access controls as follows:</Text>
                            <UnorderedList paddingLeft={6} marginTop={-5}>
                                <ListItem>
                                    individual activity – access for you (academic staff member) and your supervisor
                                </ListItem>
                                <ListItem>
                                    team activity – heads of schools can see all activity within their school
                                </ListItem>
                                <ListItem>
                                    faculty activity – faculty executives (Dean, Associate Deans, Faculty General
                                    Manager, and the PU) can view all data relevant to their faculty area within the
                                    reporting tool.
                                </ListItem>
                            </UnorderedList>
                        </VStack>
                    </VStack>
                </Box>
            </HStack>
        </Box>
    );
};

export default Privacy;
